import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DialogProps,
  Alert
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { AlertGroup, AlertTemplate } from '../types/Alerts'
import { AlertRuleDataGridRow } from './AlertRuleDataGrid'
import { Device, Project, RuuviTag } from '../types'
import { useAlertRules, usePolicy } from '../hooks'
import { AlertRuleName } from '../enums'
import { useDispatch } from 'react-redux'
import { createErrorNotification, createSuccessNotification } from '../redux/slices/notifications'
import { useState } from 'react'
import { LoadingButton } from '@mui/lab'
import { translateAlertRuleName } from '../utils/alert'
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid'
import AlertRuleLabel from './AlertRuleLabel'

export interface AlertTemplateDefaultsDialogProps extends Omit<
  DialogProps,
  'children' | 'onClose'
> {
  alertTemplates: AlertTemplate[]
  alertRules: AlertGroup[]
  project?: Project
  device?: Device
  ruuviTag?: RuuviTag
  useRedux?: boolean
  beforeSubmit?: (alertRules: AlertRuleDataGridRow[]) => void
  afterSubmit?: (alertRules: AlertGroup[]) => void
  onClose?: () => void
}

export default function AlertTemplateDefaultsDialog({
  alertTemplates,
  project,
  device,
  ruuviTag,
  open,
  maxWidth = 'md',
  fullWidth = true,
  useRedux = true,
  beforeSubmit,
  afterSubmit,
  onClose,
  ...props
}: AlertTemplateDefaultsDialogProps) {
  /**
   * The translation function.
   */
  const { t } = useTranslation('common')

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The policy state.
   */
  const { canEditProjectAlertRules, canEditDeviceAlertRules, canEditRuuviTagAlertRules } =
    usePolicy()

  /**
   * Indicates if the current user can edit alert rules of the project.
   */
  const canEditAlertRules =
    (project && canEditProjectAlertRules(project)) ||
    (device && canEditDeviceAlertRules(device)) ||
    (ruuviTag && canEditRuuviTagAlertRules(ruuviTag))

  /**
   * The pagnination model.
   */
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    pageSize: 24,
    page: 0
  })

  /**
   * The columns.
   */
  const columns: GridColDef<AlertRuleDataGridRow>[] = [
    {
      field: 'name',
      headerName: t('name'),
      flex: 1,
      width: 200,
      editable: false,
      valueGetter: (_, row) => translateAlertRuleName(row.name),
      renderCell: ({ row }) => {
        return <AlertRuleLabel alertRule={row} />
      }
    },
    {
      field: 'enabled',
      headerName: t('enabled'),
      type: 'boolean',
      width: 150,
      editable: false
    }
  ]

  /**
   * The alert rules.
   */
  const rows: AlertRuleDataGridRow[] = alertTemplates
    .map((template) =>
      Object.entries(template.defaults).map(([alertName, alert]) => ({
        id: alertName as AlertRuleName,
        group: template.name,
        name: alertName as AlertRuleName,
        enabled: alert.enabled,
        variables: alert.variables
      }))
    )
    .flat()

  const { updateAlertRules } = useAlertRules({
    project,
    device,
    ruuviTag,
    alertTemplates,
    alertRules: [],
    useRedux
  })

  /**
   * Indicates if the alert rules are updating.
   */
  const [isSubmitting, setSubmitting] = useState<boolean>(false)

  /**
   * Handle the submit.
   */
  async function handleSubmit() {
    try {
      setSubmitting(true)
      beforeSubmit?.(rows)

      const updatedAlertRules = await updateAlertRules(rows)

      dispatch(createSuccessNotification(t('updateAlertRulesSuccess')))
      setSubmitting(false)
      afterSubmit?.(updatedAlertRules)
    } catch (error) {
      console.error(error)
      dispatch(createErrorNotification(t('updateAlertRulesError')))
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} maxWidth={maxWidth} fullWidth={fullWidth} {...props} onClose={onClose}>
      <DialogTitle>{t('defaultAlertRuleValues')}</DialogTitle>
      <DialogContent sx={{ pt: 0, pb: 2 }}>
        {rows.length === 0 ? (
          <Alert severity="info">{t('noAlertRules')}</Alert>
        ) : (
          <DataGrid
            rows={rows}
            columns={columns}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            disableColumnMenu
            disableRowSelectionOnClick
            getRowHeight={() => 72}
            sx={{ border: 0 }}
          />
        )}
      </DialogContent>

      <DialogActions>
        <Button variant="text" color="inherit" onClick={onClose} autoFocus>
          {t('close')}
        </Button>

        {canEditAlertRules && (
          <LoadingButton
            variant="outlined"
            color="primary"
            loading={isSubmitting}
            onClick={handleSubmit}
          >
            {t('restoreToDefault')}
          </LoadingButton>
        )}
      </DialogActions>
    </Dialog>
  )
}
